import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { MdArrowBack } from "react-icons/md";
import axios from "axios";
import { toast } from "react-toastify";
import { useEffect } from "react";

const API_BASE_URL =
  window.location.hostname === "localhost"
    ? "https://spiderlegsproductions.com/api"
    : "/api";

export default function ChooseProfile() {
  const navigate = useNavigate();

  // 🔒 Jos käyttäjä ei ole kirjautunut, ohjataan kirjautumissivulle
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return navigate("/kirjaudu");
    }
  }, [navigate]);

  // 🔒 Jos profiilityyppi ja tilaus on jo valittu, ohjataan suoraan oikeaan dashboardiin
  useEffect(() => {
    const profileType = localStorage.getItem("profileType");
    const subscriptionPlan = localStorage.getItem("subscriptionPlan");
    if (profileType === "rental") {
      return navigate("/rental-dashboard");
    }
    if (profileType === "performer") {
      // performer ilman tilausta → tilausvalintaan
      if (!subscriptionPlan) {
        return navigate("/subscription-options");
      }
      // performer + tilaus → performer-dashboard
      return navigate("/performer-dashboard");
    }
  }, [navigate]);

  // JWT-tokenin purku
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return null;
    }
  };

  const handleSelectProfile = async (profileType) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Et ole kirjautunut sisään.");
        return navigate("/kirjaudu");
      }

      const parsedToken = parseJwt(token);
      const userId = parsedToken?.user_id;
      if (!userId) {
        toast.error("Käyttäjä-ID puuttuu.");
        return;
      }

      const { data } = await axios.post(`${API_BASE_URL}/save_profile_type.php`, {
        userId,
        profileType,
      });

      if (data.success) {
        localStorage.setItem("profileType", profileType);
        toast.success("Profiilityyppi tallennettu!");
        setTimeout(() => {
          if (profileType === "rental") {
            navigate("/rental-dashboard");
          } else {
            navigate("/subscription-options");
          }
        }, 800);
      } else {
        toast.error(data.message || "Virhe profiilin tallennuksessa.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Palvelinvirhe. Yritä hetken kuluttua uudelleen.");
    }
  };

  return (
    <div className="pt-24 pb-12 px-4 dark:bg-purple-950 text-darkest dark:text-primary dark:bg-opacity-75 min-h-screen">
      <Helmet>
        <title>Spiderlegs Productions | Valitse profiilityyppi</title>
        <meta
          name="description"
          content="Valitse oma profiilityyppisi: vuokrausprofiili tai esiintyjäprofiili Spiderlegsissä."
        />
      </Helmet>

      <ResponsiveButton
        onClick={() => {
          navigate(window.history.length > 2 ? -1 : "/");
          setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
        }}
        variant="backbutton"
        className="ml-4"
      >
        <div className="flex items-center gap-2">
          <MdArrowBack className="text-xl" />
          <span>Takaisin</span>
        </div>
      </ResponsiveButton>

      <h1 className="font-customFont2 text-center text-5xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 pt-8 dark:text-shadow-xxl">
        Valitse profiilityyppi
      </h1>

      <p className="max-w-3xl mx-auto mb-12 font-customFont1 dark:text-shadow-lg text-center text-darkest dark:text-primary font-normal text-lg">
        Haluatko vuokrata laitteita tai liittyä esiintyjäverkostoon? Valitse sopiva profiili ja aloita!
      </p>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Vuokrausprofiili */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => handleSelectProfile("rental")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleSelectProfile("rental");
            }
          }}
          className="border border-gray-300 dark:border-gray-600 dark:bg-violet bg-primary bg-opacity-75 dark:bg-opacity-75 py-10 p-8 rounded-3xl text-center cursor-pointer hover:scale-105 transition shadow-md"
        >
          <h2 className="text-3xl font-customFont2 font-bold mb-4">Vuokrausprofiili</h2>
          <p className="font-customFont1 text-lg">
            Vuokraa äänentoisto- ja tapahtumatekniikkalaitteita helposti. Näe ja hallinnoi omia varauksiasi.
          </p>
        </div>

        {/* Esiintyjäprofiili */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => handleSelectProfile("performer")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleSelectProfile("performer");
            }
          }}
          className="border border-yellow-400 dark:border-yellow-500 dark:bg-violet bg-primary bg-opacity-75 dark:bg-opacity-75 py-10 p-8 rounded-3xl text-center cursor-pointer hover:scale-105 transition shadow-md"
        >
          <h2 className="text-3xl font-customFont2 font-bold mb-4 text-yellow-500">Esiintyjäprofiili</h2>
          <p className="font-customFont1 text-lg">
            Liity esiintyjäverkostoon, rakenna oma profiili, vastaanota keikkatarjouksia ja vuokraa myös laitteita.
          </p>
        </div>
      </div>
    </div>
  );
}
